// src/components/GlobalFloating.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./GlobalFloating.css";

import { CONFIG } from "../config";
/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;

// derive platform from URL if not provided
const keyFromUrl = (link = "") => {
  try {
    const h = new URL(link).hostname.toLowerCase();
    if (h.includes("instagram")) return "instagram";
    if (h.includes("facebook")) return "facebook";
    if (h.includes("linkedin")) return "linkedin";
    if (h.includes("youtube")) return "youtube";
    if (h.includes("x.com") || h.includes("twitter")) return "x";
    if (h.includes("whatsapp")) return "whatsapp";
    if (h.includes("telegram")) return "telegram";
    return "website";
  } catch {
    return "website";
  }
};

// always build from backend
const apiIcon = (filenameOrPlatform) =>
  `${IMAGES_BASE}/${encodeURIComponent(filenameOrPlatform)}`;

const sanitizePhone = (p = "") => String(p).replace(/[^\d+]/g, "");

export default function GlobalFloating() {
  const [cfg, setCfg] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    const off = onValue(ref(db, "contactconfig"), (snap) => {
      if (!mounted.current) return;
      setCfg(snap.val() || {});
    });
    return () => {
      mounted.current = false;
      if (typeof off === "function") off();
    };
  }, []);

  // Build items from config
  const items = useMemo(() => {
    if (!cfg) return [];
    const out = [];

    // PHONE (icon filename must come from backend or fallback to phone.svg on backend)
    if (cfg?.phone?.floatingIcon && cfg?.phone?.number) {
      const tel = sanitizePhone(cfg.phone.number);
      const iconFile = cfg?.phone?.icon || "phone.svg"; // backend file name
      out.push({
        key: "phone",
        href: `tel:${tel}`,
        label: cfg.phone.number,
        icon: apiIcon(iconFile),
      });
    }

    // EMAIL
    if (cfg?.email?.floatingIcon && cfg?.email?.value) {
      const iconFile = cfg?.email?.icon || "email.svg"; // backend file name
      out.push({
        key: "email",
        href: `mailto:${cfg.email.value}`,
        label: cfg.email.value,
        icon: apiIcon(iconFile),
      });
    }

    // SOCIALS (ANY with floatingIcon: true)
    const socialArr = Array.isArray(cfg?.socialmedia) ? cfg.socialmedia : [];
    socialArr
      .filter((s) => s?.link && s?.floatingIcon)
      .forEach((s) => {
        const platform = (s?.platform || keyFromUrl(s.link)).toLowerCase();
        // prefer uploaded icon from backend; else use <platform>.svg from backend
        const iconFile = s?.icon || `${platform}.svg`;
        out.push({
          key: platform,
          href: s.link,
          label: platform,
          icon: apiIcon(iconFile),
        });
      });

    return out;
  }, [cfg]);

  // Global floating style from contactconfig
  const { fabSize, borderRadius } = useMemo(() => {
    const shape = (cfg?.floatingShape || "circle").toLowerCase();
    const sz = Number(cfg?.floatingSize);
    const sizePx = !Number.isNaN(sz) && sz > 20 ? sz : 56;

    let br = "999px"; // circle default
    if (shape === "square") br = "0px";
    else if (shape === "rounded") br = "16px";

    return { fabSize: sizePx, borderRadius: br };
  }, [cfg?.floatingShape, cfg?.floatingSize]);

  if (!items.length) return null;

  return (
    <div className="fab-wrap">
      {items.map((it) => (
        <a
          key={`${it.key}-${it.href}`}
          className={`fab ${it.key}`}
          href={it.href}
          target={it.href.startsWith("http") ? "_blank" : undefined}
          rel={it.href.startsWith("http") ? "noopener noreferrer" : undefined}
          aria-label={it.label || it.key}
          title={it.label || it.key}
          style={{
            width: `${fabSize}px`,
            height: `${fabSize}px`,
            borderRadius,
          }}
        >
          <img
            src={it.icon}
            alt={it.key}
            style={{
              width: "65%",
              height: "65%",
              objectFit: "contain",
            }}
          />
        </a>
      ))}
    </div>
  );
}
